import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormLabelDirective,
  FormSelectDirective,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  Appointment,
  AppointmentAssignments,
  AppointmentCalendar,
  AppointmentRequest,
  AppointmentRole,
  AppointmentService,
  AppointmentStatus,
  AppointmentStatusFilter,
  RecurrenceRule,
  WEEKDAY_RULES
} from '../../../services/appointment.service';

/** The zone the organisers think in — shown next to every appointment's own zone. */
const REFERENCE_TIME_ZONE = 'Europe/Berlin';

/** Validation codes the backend returns (`codes`), each with its own message. */
const KNOWN_ERROR_CODES = ['calendars_required', 'audiences_required', 'invalid_calendar', 'invalid_role', 'invalid_occurrence_date'];

/** State of the cancel panel. */
interface CancelDraft {
  appointment: Appointment;
  scope: 'series' | 'occurrence';
  occurrenceDate: string;
  note: string;
}

/**
 * Admin overview of the central calendar (Phase 1).
 *
 * One appointment is one row, tenant-wide: which boards' calendars show it (A)
 * and which community roles see it (B) are edited inline and take effect at once
 * — also after release. Creating or editing never publishes: a new appointment
 * is a draft until somebody presses "Release".
 *
 * The form deliberately collects a *wall-clock* date and time plus a zone, and
 * sends exactly that. Letting the browser turn the input into a UTC instant would
 * silently record the admin's own zone instead of the one they picked, and a
 * weekly series would then drift by an hour at every DST switch.
 */
@Component({
  selector: 'app-admin-appointments-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TableDirective,
    BadgeComponent,
    ButtonDirective,
    FormControlDirective,
    FormLabelDirective,
    FormSelectDirective,
    RouterLink,
    TranslatePipe
  ],
  templateUrl: './admin-appointments-page.component.html',
  styleUrls: ['./admin-appointments-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminAppointmentsPageComponent {
  private logger = new Logger('AdminAppointmentsPageComponent');
  private service = inject(AppointmentService);
  private translate = inject(TranslateService);
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  /** Must match Appointment::RECURRENCE_RULES on the backend. */
  readonly rules: RecurrenceRule[] = ['none', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly'];

  readonly statuses: AppointmentStatusFilter[] = ['all', 'draft', 'released', 'cancelled', 'inactive'];

  /** Monday first: these brands' admins are in Europe and the Americas alike, but
   *  the ISO week is what both the date pickers and the copy assume. */
  readonly weekdayOrder = [1, 2, 3, 4, 5, 6, 0];

  readonly referenceTimeZone = REFERENCE_TIME_ZONE;

  appointments = signal<Appointment[]>([]);
  calendars = signal<AppointmentCalendar[]>([]);
  roles = signal<AppointmentRole[]>([]);
  timeZones = signal<string[]>([]);
  languages = signal<string[]>([]);
  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);
  notice = signal<string | null>(null);

  // Filters (server side).
  filterCalendar = signal<string>('');
  filterRole = signal<string>('');
  filterStatus = signal<AppointmentStatusFilter>('all');
  filterQuery = signal<string>('');
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  /** Row multi-select for the bulk release. */
  selected = signal<Set<number>>(new Set());
  /** Rows with an inline change in flight — their controls are disabled meanwhile. */
  busyRows = signal<Set<number>>(new Set());
  /** Row whose audience picker is open (inline, below the chips). */
  audiencePickerFor = signal<number | null>(null);

  cancelDraft = signal<CancelDraft | null>(null);

  showForm = signal(false);
  /** null while creating; the record being edited otherwise. */
  editing = signal<Appointment | null>(null);
  form = signal<AppointmentRequest>(this.emptyForm());
  showTranslations = signal(false);

  /** Weekday checkboxes only apply to the weekly rules — see WEEKDAY_RULES. */
  showWeekdays = computed(() => WEEKDAY_RULES.includes(this.form().recurrence_rule));
  showRecurrenceOptions = computed(() => this.form().recurrence_rule !== 'none');

  /** Every language a translation can be entered for — all but the primary one. */
  translationLanguages = computed(() => {
    const primary = this.form().language;
    const known = new Set(this.languages());
    // A translation stored for a language no board offers any more stays editable.
    Object.keys(this.form().title_i18n ?? {}).forEach(lang => known.add(lang));
    Object.keys(this.form().details_i18n ?? {}).forEach(lang => known.add(lang));
    return [...known].filter(lang => lang && lang !== primary);
  });

  selectedDraftCount = computed(() => {
    const ids = this.selected();
    return this.appointments().filter(a => ids.has(a.id) && a.status === 'draft').length;
  });

  allSelected = computed(() => {
    const list = this.appointments();
    return list.length > 0 && list.every(a => this.selected().has(a.id));
  });

  constructor() {
    this.load();
    this.destroyRef.onDestroy(() => {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
    });
  }

  private emptyForm(): AppointmentRequest {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');

    return {
      language: this.translate.getActiveLang() || 'en',
      title: '',
      details: '',
      title_i18n: {},
      details_i18n: {},
      join_url: '',
      location: '',
      local_date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      local_time: '19:00',
      duration_minutes: 60,
      // Pre-filled with the admin's own zone, which is almost always the one the
      // call is announced in — but it stays an explicit, visible choice.
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      recurrence_rule: 'none',
      recurrence_weekdays: [],
      recurrence_until: '',
      active: true,
      publish_to_news: false,
      calendar_ids: [],
      audience_role_ids: [],
      internal_only: false
    };
  }

  private load(): void {
    this.loading.set(true);

    this.service.getAll({
      calendar: this.filterCalendar() ? Number(this.filterCalendar()) : null,
      role: this.filterRole() || null,
      status: this.filterStatus(),
      q: this.filterQuery()
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.appointments.set(response.appointments ?? []);
          this.calendars.set(response.calendars ?? []);
          this.roles.set(response.roles ?? []);
          this.languages.set(response.languages ?? []);
          // The admin's own zone is offered even when it is not on the backend's
          // curated list — otherwise the pre-filled value would not be selectable.
          const own = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const zones = response.time_zones ?? [];
          this.timeZones.set((zones.includes(own) ? zones : [own, ...zones]).filter(Boolean));
          // Drop selections that the new filter result no longer contains.
          const visible = new Set(this.appointments().map(a => a.id));
          this.selected.update(current => new Set([...current].filter(id => visible.has(id))));
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load appointments:', err);
          this.error.set(err?.error?.error || this.translate.translate('appointments.admin.load_failed'));
          this.loading.set(false);
        }
      });
  }

  // ---------------------------------------------------------------- filters

  onFilterCalendar(value: string): void {
    this.filterCalendar.set(value ?? '');
    this.load();
  }

  onFilterRole(value: string): void {
    this.filterRole.set(value ?? '');
    this.load();
  }

  onFilterStatus(value: AppointmentStatusFilter): void {
    this.filterStatus.set(value || 'all');
    this.load();
  }

  onFilterQuery(value: string): void {
    this.filterQuery.set(value ?? '');
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.load(), 350);
  }

  // -------------------------------------------------------------- selection

  isSelected(appointment: Appointment): boolean {
    return this.selected().has(appointment.id);
  }

  toggleSelected(appointment: Appointment): void {
    this.selected.update(current => {
      const next = new Set(current);
      next.has(appointment.id) ? next.delete(appointment.id) : next.add(appointment.id);
      return next;
    });
  }

  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.selected.set(new Set());
    } else {
      this.selected.set(new Set(this.appointments().map(a => a.id)));
    }
  }

  // ---------------------------------------------------- inline assignments

  hasCalendar(appointment: Appointment, calendarId: number): boolean {
    return (appointment.calendar_ids ?? []).includes(calendarId);
  }

  hasAudience(appointment: Appointment, roleId: number): boolean {
    return (appointment.audience_role_ids ?? []).includes(roleId);
  }

  isBusy(appointment: Appointment): boolean {
    return this.busyRows().has(appointment.id);
  }

  toggleCalendar(appointment: Appointment, calendarId: number, event?: Event): void {
    const ids = appointment.calendar_ids ?? [];
    const calendarIds = ids.includes(calendarId)
      ? ids.filter(id => id !== calendarId)
      : [...ids, calendarId].sort((a, b) => a - b);

    if (calendarIds.length === 0) {
      this.error.set(this.translate.translate('appointments.admin.errors.calendars_required'));
      this.revertCheckbox(event);
      return;
    }

    this.patchAssignments(appointment, { ...this.assignmentsOf(appointment), calendar_ids: calendarIds });
  }

  toggleAudience(appointment: Appointment, roleId: number, event?: Event): void {
    const ids = appointment.audience_role_ids ?? [];
    const roleIds = ids.includes(roleId) ? ids.filter(id => id !== roleId) : [...ids, roleId];

    if (roleIds.length === 0 && !appointment.internal_only) {
      this.error.set(this.translate.translate('appointments.admin.errors.audiences_required'));
      this.revertCheckbox(event);
      return;
    }

    this.patchAssignments(appointment, { ...this.assignmentsOf(appointment), audience_role_ids: roleIds });
  }

  toggleInternal(appointment: Appointment, event?: Event): void {
    const internalOnly = !appointment.internal_only;
    if (!internalOnly && (appointment.audience_role_ids ?? []).length === 0) {
      this.error.set(this.translate.translate('appointments.admin.errors.audiences_required'));
      this.revertCheckbox(event);
      return;
    }
    this.patchAssignments(appointment, { ...this.assignmentsOf(appointment), internal_only: internalOnly });
  }

  /**
   * The browser has already flipped the box; the bound value did not change, so
   * Angular would not write it back — undo the flip by hand.
   */
  private revertCheckbox(event?: Event): void {
    const input = event?.target as HTMLInputElement | undefined;
    if (input && 'checked' in input) {
      input.checked = !input.checked;
    }
  }

  toggleAudiencePicker(appointment: Appointment): void {
    this.audiencePickerFor.update(current => (current === appointment.id ? null : appointment.id));
  }

  /** Chips for the audience column — names from the role list, falling back to the row's own. */
  audienceChips(appointment: Appointment): { id: number; name: string }[] {
    const byId = new Map(this.roles().map(role => [role.id, role.name]));
    const own = new Map((appointment.audiences ?? []).map(role => [role.id, role.name]));
    return (appointment.audience_role_ids ?? []).map(id => ({
      id,
      name: byId.get(id) ?? own.get(id) ?? `#${id}`
    }));
  }

  private assignmentsOf(appointment: Appointment): AppointmentAssignments {
    return {
      calendar_ids: [...(appointment.calendar_ids ?? [])],
      audience_role_ids: [...(appointment.audience_role_ids ?? [])],
      internal_only: appointment.internal_only === true
    };
  }

  /**
   * Optimistic: the row shows the change at once and is rolled back if the
   * backend refuses it — an inline checkbox that waits for a round trip feels
   * broken, one that silently lies would be worse.
   */
  private patchAssignments(appointment: Appointment, assignments: AppointmentAssignments): void {
    const previous = appointment;
    this.error.set(null);
    this.replaceRow({ ...appointment, ...assignments });
    this.setBusy(appointment.id, true);

    this.service.patchAssignments(appointment.id, assignments)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (response?.appointment) {
            this.replaceRow(response.appointment);
          }
          this.setBusy(appointment.id, false);
        },
        error: err => {
          this.logger.error('Failed to change assignments:', err);
          this.replaceRow(previous);
          this.setBusy(appointment.id, false);
          this.error.set(this.errorMessage(err, 'appointments.admin.assignments_failed'));
        }
      });
  }

  private replaceRow(appointment: Appointment): void {
    this.appointments.update(list => list.map(row => (row.id === appointment.id ? appointment : row)));
  }

  private setBusy(id: number, busy: boolean): void {
    this.busyRows.update(current => {
      const next = new Set(current);
      busy ? next.add(id) : next.delete(id);
      return next;
    });
  }

  // ---------------------------------------------------------------- release

  release(appointment: Appointment): void {
    if (!confirm(this.translate.translate('appointments.admin.release_confirm'))) {
      return;
    }
    this.error.set(null);
    this.notice.set(null);
    this.setBusy(appointment.id, true);

    this.service.release(appointment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.setBusy(appointment.id, false);
          this.notice.set(this.translate.translate('appointments.admin.released_one'));
          // Releasing also retires the appointments it replaces — reload, not patch.
          this.load();
        },
        error: err => {
          this.logger.error('Failed to release appointment:', err);
          this.setBusy(appointment.id, false);
          this.error.set(this.errorMessage(err, 'appointments.admin.release_failed'));
        }
      });
  }

  releaseSelected(): void {
    const ids = this.appointments()
      .filter(a => this.selected().has(a.id) && a.status === 'draft')
      .map(a => a.id);
    if (ids.length === 0) {
      return;
    }
    if (!confirm(this.translate.translate('appointments.admin.release_many_confirm', { count: ids.length }))) {
      return;
    }
    this.error.set(null);
    this.notice.set(null);

    this.service.releaseMany(ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const released = response?.released?.length ?? 0;
          this.notice.set(this.translate.translate('appointments.admin.released_many', { count: released }));
          const failed = response?.failed ?? [];
          if (failed.length) {
            this.error.set(
              this.translate.translate('appointments.admin.release_many_failed') + ' ' +
              failed.map(f => `#${f.id}: ${(f.errors ?? []).join(', ')}`).join(' · ')
            );
          }
          this.selected.set(new Set());
          this.load();
        },
        error: err => {
          this.logger.error('Failed to release appointments:', err);
          this.error.set(this.errorMessage(err, 'appointments.admin.release_failed'));
        }
      });
  }

  // ----------------------------------------------------------------- cancel

  startCancel(appointment: Appointment): void {
    this.error.set(null);
    this.notice.set(null);
    this.cancelDraft.set({
      appointment,
      scope: appointment.recurrence_rule === 'none' ? 'series' : 'occurrence',
      occurrenceDate: appointment.next_occurrence?.local_date ?? appointment.local_date,
      note: appointment.cancellation_note ?? ''
    });
  }

  patchCancel<K extends keyof CancelDraft>(key: K, value: CancelDraft[K]): void {
    this.cancelDraft.update(current => (current ? { ...current, [key]: value } : current));
  }

  closeCancel(): void {
    this.cancelDraft.set(null);
  }

  confirmCancel(): void {
    const draft = this.cancelDraft();
    if (!draft) {
      return;
    }
    if (draft.scope === 'occurrence' && !draft.occurrenceDate) {
      this.error.set(this.translate.translate('appointments.admin.cancel_panel.date_required'));
      return;
    }
    this.saving.set(true);
    this.error.set(null);

    this.service.cancel(draft.appointment.id, {
      note: draft.note.trim(),
      occurrence_date: draft.scope === 'occurrence' ? draft.occurrenceDate : null
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.cancelDraft.set(null);
          this.notice.set(this.translate.translate('appointments.admin.cancel_panel.done'));
          this.load();
        },
        error: err => {
          this.logger.error('Failed to cancel appointment:', err);
          this.saving.set(false);
          this.error.set(this.errorMessage(err, 'appointments.admin.cancel_panel.failed'));
        }
      });
  }

  /** Takes back the series cancellation (no date) or one cancelled occurrence. */
  uncancel(appointment: Appointment, occurrenceDate?: string): void {
    this.saving.set(true);
    this.error.set(null);

    this.service.uncancel(appointment.id, { occurrence_date: occurrenceDate ?? null })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.saving.set(false);
          if (response?.appointment) {
            this.replaceRow(response.appointment);
            const draft = this.cancelDraft();
            if (draft && draft.appointment.id === appointment.id) {
              this.cancelDraft.set({ ...draft, appointment: response.appointment });
            }
          }
          this.notice.set(this.translate.translate('appointments.admin.cancel_panel.undone'));
          this.load();
        },
        error: err => {
          this.logger.error('Failed to take back the cancellation:', err);
          this.saving.set(false);
          this.error.set(this.errorMessage(err, 'appointments.admin.cancel_panel.failed'));
        }
      });
  }

  // ------------------------------------------------------------------- form

  patch<K extends keyof AppointmentRequest>(key: K, value: AppointmentRequest[K]): void {
    this.form.update(current => ({ ...current, [key]: value }));
  }

  startCreate(): void {
    this.editing.set(null);
    const form = this.emptyForm();
    // A brand-new appointment starts in the admin's current language if a board offers it.
    if (this.languages().length && !this.languages().includes(form.language)) {
      form.language = this.languages()[0];
    }
    this.form.set(form);
    this.showTranslations.set(false);
    this.error.set(null);
    this.notice.set(null);
    this.showForm.set(true);
  }

  startEdit(appointment: Appointment): void {
    this.editing.set(appointment);
    this.error.set(null);
    this.notice.set(null);
    this.form.set({
      language: appointment.language,
      title: appointment.title,
      details: appointment.details ?? '',
      title_i18n: { ...(appointment.title_i18n ?? {}) },
      details_i18n: { ...(appointment.details_i18n ?? {}) },
      join_url: appointment.join_url ?? '',
      location: appointment.location ?? '',
      local_date: appointment.local_date,
      local_time: appointment.local_time,
      duration_minutes: appointment.duration_minutes,
      time_zone: appointment.time_zone,
      recurrence_rule: appointment.recurrence_rule,
      recurrence_weekdays: [...(appointment.recurrence_weekdays ?? [])],
      recurrence_until: appointment.recurrence_until ?? '',
      active: appointment.active,
      publish_to_news: appointment.publish_to_news === true,
      calendar_ids: [...(appointment.calendar_ids ?? [])],
      audience_role_ids: [...(appointment.audience_role_ids ?? [])],
      internal_only: appointment.internal_only === true
    });
    const hasTranslations = Object.values(appointment.title_i18n ?? {}).some(Boolean)
      || Object.values(appointment.details_i18n ?? {}).some(Boolean);
    this.showTranslations.set(hasTranslations);
    this.showForm.set(true);
  }

  cancel(): void {
    this.showForm.set(false);
    this.editing.set(null);
    this.error.set(null);
  }

  hasWeekday(day: number): boolean {
    return (this.form().recurrence_weekdays ?? []).includes(day);
  }

  toggleWeekday(day: number): void {
    this.form.update(current => {
      const days = current.recurrence_weekdays ?? [];
      return {
        ...current,
        recurrence_weekdays: days.includes(day)
          ? days.filter(existing => existing !== day)
          : [...days, day].sort((a, b) => a - b)
      };
    });
  }

  formHasCalendar(calendarId: number): boolean {
    return (this.form().calendar_ids ?? []).includes(calendarId);
  }

  formToggleCalendar(calendarId: number): void {
    this.form.update(current => {
      const ids = current.calendar_ids ?? [];
      return {
        ...current,
        calendar_ids: ids.includes(calendarId)
          ? ids.filter(id => id !== calendarId)
          : [...ids, calendarId].sort((a, b) => a - b)
      };
    });
  }

  formHasRole(roleId: number): boolean {
    return (this.form().audience_role_ids ?? []).includes(roleId);
  }

  formToggleRole(roleId: number): void {
    this.form.update(current => {
      const ids = current.audience_role_ids ?? [];
      return {
        ...current,
        audience_role_ids: ids.includes(roleId) ? ids.filter(id => id !== roleId) : [...ids, roleId]
      };
    });
  }

  translationOf(field: 'title_i18n' | 'details_i18n', lang: string): string {
    return this.form()[field]?.[lang] ?? '';
  }

  setTranslation(field: 'title_i18n' | 'details_i18n', lang: string, value: string): void {
    this.form.update(current => ({ ...current, [field]: { ...(current[field] ?? {}), [lang]: value } }));
  }

  hasTranslation(lang: string): boolean {
    return !!(this.translationOf('title_i18n', lang).trim() || this.translationOf('details_i18n', lang).trim());
  }

  save(): void {
    const current = this.form();
    // Names what is missing. The generic "could not be saved" reads like a server
    // failure and sends the admin looking in the wrong place.
    if (!current.title.trim() || !current.local_date || !current.local_time) {
      this.error.set(this.translate.translate('appointments.admin.required_fields'));
      return;
    }
    if (!(current.calendar_ids ?? []).length) {
      this.error.set(this.translate.translate('appointments.admin.errors.calendars_required'));
      return;
    }
    if (!current.internal_only && !(current.audience_role_ids ?? []).length) {
      this.error.set(this.translate.translate('appointments.admin.errors.audiences_required'));
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    const payload: AppointmentRequest = {
      ...current,
      title: current.title.trim(),
      details: current.details?.trim() || null,
      title_i18n: this.compactTranslations(current.title_i18n, current.language),
      details_i18n: this.compactTranslations(current.details_i18n, current.language),
      join_url: current.join_url?.trim() || null,
      location: current.location?.trim() || null,
      // Empty strings would fail the backend's date cast; nil is "open ended".
      recurrence_until: current.recurrence_until || null,
      duration_minutes: current.duration_minutes || null,
      recurrence_weekdays: this.showWeekdays() ? (current.recurrence_weekdays ?? []) : []
    };

    const editing = this.editing();
    const request$ = editing
      ? this.service.update(editing.id, payload)
      : this.service.create(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.editing.set(null);
        // A new appointment is always a draft — say so, or the admin wonders why
        // members do not see it.
        this.notice.set(editing ? null : this.translate.translate('appointments.admin.created_as_draft'));
        this.load();
      },
      error: err => {
        this.logger.error('Failed to save appointment:', err);
        this.error.set(this.errorMessage(err, 'appointments.admin.save_failed'));
        this.saving.set(false);
      }
    });
  }

  /** Empty translations are dropped: an empty field means "fall back", not "show nothing". */
  private compactTranslations(map: Record<string, string> | undefined, primary: string): Record<string, string> {
    const result: Record<string, string> = {};
    Object.entries(map ?? {}).forEach(([lang, text]) => {
      const value = (text ?? '').trim();
      if (value && lang !== primary) {
        result[lang] = value;
      }
    });
    return result;
  }

  remove(appointment: Appointment): void {
    if (!confirm(this.translate.translate('appointments.admin.delete_confirm'))) {
      return;
    }

    this.service.delete(appointment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.load(),
        error: err => {
          this.logger.error('Failed to delete appointment:', err);
          this.error.set(err?.error?.error || this.translate.translate('appointments.admin.delete_failed'));
        }
      });
  }

  // ---------------------------------------------------------------- display

  statusColor(status: AppointmentStatus): string {
    switch (status) {
      case 'draft': return 'warning';
      case 'released': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  }

  /** "Weekly · Tue, Thu · until 2026-12-31" — the rule name alone does not say which days. */
  seriesSummary(appointment: Appointment): string {
    const parts = [this.translate.translate(`appointments.recurrence.${appointment.recurrence_rule}`)];
    if (appointment.recurrence_rule !== 'none') {
      const days = this.weekdaySummary(appointment);
      if (days) {
        parts.push(days);
      }
      parts.push(appointment.recurrence_until
        ? this.translate.translate('appointments.admin.series_until', { date: appointment.recurrence_until })
        : this.translate.translate('appointments.admin.series_open_ended'));
    }
    return parts.join(' · ');
  }

  weekdaySummary(appointment: Appointment): string {
    if (!appointment.recurrence_weekdays?.length) {
      return '';
    }
    return appointment.recurrence_weekdays
      .map(day => this.translate.translate(`appointments.weekday_short.d${day}`))
      .join(', ');
  }

  /** The next occurrence, 24h, in the appointment's own zone. */
  nextInOwnZone(appointment: Appointment): string | null {
    const next = appointment.next_occurrence;
    return next ? this.formatInZone(next.starts_at, appointment.time_zone) : null;
  }

  /** The same moment in Europe/Berlin — null when the appointment's zone already is it. */
  nextInReferenceZone(appointment: Appointment): string | null {
    const next = appointment.next_occurrence;
    if (!next || this.sameOffset(next.starts_at, appointment.time_zone, REFERENCE_TIME_ZONE)) {
      return null;
    }
    return this.formatInZone(next.starts_at, REFERENCE_TIME_ZONE);
  }

  calendarName(calendarId: number): string {
    return this.calendars().find(calendar => calendar.id === calendarId)?.name ?? `#${calendarId}`;
  }

  newsThreadLink(threadId: number): string {
    return `/${this.i18n.getLanguage()}/community/${threadId}`;
  }

  /** "#12, #15" for the "replaces" hint. */
  idList(ids: number[]): string {
    return ids.map(id => `#${id}`).join(', ');
  }

  private formatInZone(iso: string, timeZone: string): string {
    const date = new Date(iso);
    try {
      return new Intl.DateTimeFormat(this.translate.getActiveLang() || 'en', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone
      }).format(date);
    } catch {
      // An unknown zone name must not break the whole table.
      return date.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
    }
  }

  private sameOffset(iso: string, zoneA: string, zoneB: string): boolean {
    if (zoneA === zoneB) {
      return true;
    }
    const fmt = (zone: string) => {
      try {
        return new Intl.DateTimeFormat('en-GB', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
          hourCycle: 'h23', timeZone: zone
        }).format(new Date(iso));
      } catch {
        return zone;
      }
    };
    return fmt(zoneA) === fmt(zoneB);
  }

  /** Maps the backend's validation codes to their own messages; falls back to its texts. */
  private errorMessage(err: any, fallbackKey: string): string {
    const codes: string[] = err?.error?.codes ?? [];
    const known = codes.filter(code => KNOWN_ERROR_CODES.includes(code));
    if (known.length) {
      return known.map(code => this.translate.translate(`appointments.admin.errors.${code}`)).join(' ');
    }
    return err?.error?.errors?.join(', ') || err?.error?.error || this.translate.translate(fallbackKey);
  }
}
